/*
 * The reveal.
 *
 * A short rise and a fade, and nothing else. No scale, no blur, no spring: the
 * brand is flat and rectangular, and motion that bounces would be the one place
 * in the system where something behaves like a different product.
 *
 * The hidden state is gated on `data-armed`, which the component only sets
 * after it has mounted. That ordering is the whole safety property — without
 * JavaScript, or before hydration, nothing here hides anything.
 */

.reveal {
  transition:
    opacity var(--gb-motion-slow) var(--gb-ease-out),
    transform var(--gb-motion-slow) var(--gb-ease-out);
}

.reveal[data-armed='true'] {
  opacity: 0;
  transform: translateY(0.875rem);
}

.reveal[data-armed='true'][data-shown='true'] {
  opacity: 1;
  transform: none;
}

/*
 * The other way in: an edge, not a fade.
 *
 * The block is uncovered from behind a hard horizontal line travelling up
 * through it, at full opacity the whole way. That is a title card, and it is
 * closer to what this brand is than the rise above — flat rectangles, an edge
 * doing the work, nothing softening. `clip-path` rather than a masking element,
 * because a mask would need a ground colour to paint and this has to work over
 * a flood band, over the page and over capsule art alike.
 *
 * The opacity is restated rather than left to the rule above, and it has to be:
 * `.reveal[data-armed='true']` is what puts a block at nought, and a variant
 * that only sets a clip would arrive as a fade *and* a wipe. Three attributes
 * of specificity beats two, so this wins without an `!important`.
 *
 * **The resting state is a clip at the border box, which is the one thing a
 * caller has to know.** `clip-path` cannot be transitioned to `none`, so the
 * end of the wipe is `inset(0)` and it stays there — harmless around type,
 * which never draws outside its own box, and destructive around anything that
 * does. The capsule is the case: its accent halo stands twelve pixels proud of
 * the card by design, and a wiped section containing a shelf would shave that
 * off permanently. Wrap prose and headings with this; wrap art with `rise`.
 */
.reveal[data-variant='wipe'] {
  transition:
    clip-path var(--gb-motion-slow) var(--gb-ease-out),
    opacity var(--gb-motion-slow) var(--gb-ease-out);
}

.reveal[data-armed='true'][data-variant='wipe'] {
  opacity: 1;
  transform: none;
  clip-path: inset(0 0 100% 0);
}

.reveal[data-armed='true'][data-shown='true'][data-variant='wipe'] {
  clip-path: inset(0 0 0 0);
}

/* --- The scene change --------------------------------------------------- */

/*
 * The layer the blade travels across: the window, and nothing else in it.
 *
 * `fixed` rather than absolute, so the sweep is across the *window* rather than
 * across a page that may be twelve screens long — the transition is about
 * arriving somewhere, which is a fact about the viewport.
 *
 * Above the sticky bar (20) and the select's open list (30) and the loader over
 * a page (40), and below the boot screen (60), which renders instead of the
 * application rather than over it.
 *
 * `pointer-events: none` is not a nicety. Everything here is scenery, and a
 * full-window element that ate a click for a third of a second after every
 * navigation would be the single worst bug this file could ship — it would look
 * exactly like the site having stopped responding.
 */
.scene {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100dvh;
  overflow: hidden;
  pointer-events: none;
  z-index: 50;
}

/*
 * The blade.
 *
 * A flat field of the panel's accent, a seventh of the window wide, crossing it
 * left to right at a constant speed. `linear` for the same reason the boot
 * screen's sweep is linear: both ends of the travel happen off the edge of the
 * track where an ease cannot be seen, and what an ease *would* be visible as is
 * the block slowing through the middle, which reads as a hesitation rather than
 * as a cut.
 *
 * It is parked one blade-width off the left edge and travels the window plus
 * that width, so the accent is fully outside the frame at both ends and there
 * is never a moment with a stripe resting against an edge. The width is one
 * custom property so the two arithmetics cannot disagree — the keyframe reads
 * the same `--blade` the offset does.
 *
 * `transform` only. A blade animating `left` is a layout on every frame of
 * every navigation, on a full-height element.
 *
 * The duration is the slow token doubled, which is about two thirds of a
 * second: long enough to read as a cut between two places, short enough that it
 * is over before anybody has finished looking at the page it revealed.
 *
 * `backwards` and never `forwards`. A forwards fill would hold the blade at the
 * end of its travel for the rest of the visit, which is a full-height bar of
 * accent pinned outside the right edge — invisible here only because `.scene`
 * clips, which is a thing that could be changed by somebody who did not know it
 * was carrying that. Without the fill the element simply reverts to its resting
 * offset, off the left edge, which is where it should be when nothing is
 * happening.
 */
.sceneBlade {
  /* A seventh of the window, and never so thin on a phone that it reads as a
     hairline rather than as a field. */
  --blade: max(5rem, 14vw);

  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(var(--blade) * -1);
  width: var(--blade);
  background: var(--gb-accent);
  animation: gb-scene-wipe calc(var(--gb-motion-slow) * 2) linear backwards;
}

@keyframes gb-scene-wipe {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(calc(100vw + var(--blade)));
  }
}

/*
 * Belt and braces. The component already skips the observer entirely under
 * reduced motion, but a stylesheet that can be relied on not to hide anything
 * is worth more than one that depends on a script having run correctly.
 *
 * The scene change goes entirely rather than instantly: the global reset in
 * `tokens.css` collapses an animation to one 0.01ms iteration, and a blade that
 * crosses the window in a hundredth of a millisecond is a flash of the accent
 * over the whole page on every navigation — which is not a shortened animation,
 * it is a different and much worse one.
 */
@media (prefers-reduced-motion: reduce) {
  .reveal,
  .reveal[data-armed='true'] {
    opacity: 1;
    transform: none;
    transition: none;
    transition-delay: 0s !important;
  }

  .reveal[data-armed='true'][data-variant='wipe'] {
    clip-path: none;
  }

  .scene {
    display: none;
  }
}
